/**
 * Cryptographically secure random number generation
 * Addresses CodeQL warning about insecure randomness (Math.random).
 *
 * Uses:
 * - Browser: crypto.getRandomValues (Web Crypto API)
 * - Node.js: crypto.randomInt (built-in crypto module)
 *
 * Implements rejection sampling to avoid modulo bias.
 */

/**
 * Generate a cryptographically secure random integer in [0, max).
 *
 * @param max - Upper bound (exclusive), must be positive
 * @returns Random integer between 0 (inclusive) and max (exclusive)
 * @throws Error if max <= 0 or no secure RNG available
 *
 * @example
 * const diceRoll = getSecureRandomInt(6) + 1; // 1-6
 * const wordIndex = getSecureRandomInt(wordList.length);
 */
export function getSecureRandomInt(max: number): number {
  if (max <= 0) {
    throw new Error('max must be positive');
  }

  // Browser environment
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    // Use rejection sampling to avoid modulo bias
    // Generate random uint32, reject values that would cause bias
    const maxUint = 0xFFFFFFFF; // 2^32 - 1
    const limit = Math.floor((maxUint + 1) / max) * max;

    const arr = new Uint32Array(1);
    let r = window.crypto.getRandomValues(arr)[0];

    // Reject and regenerate if value is in biased range
    while (r >= limit) {
      r = window.crypto.getRandomValues(arr)[0];
    }

    return r % max;
  }

  // Node.js environment
  if (typeof require !== 'undefined') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const nodeCrypto = require('crypto');

      if (typeof nodeCrypto.randomInt === 'function') {
        // crypto.randomInt is already bias-free
        return nodeCrypto.randomInt(0, max);
      }
    } catch (err) {
      // Fall through to error
    }
  }

  throw new Error('No secure RNG available');
}

/**
 * Generate a cryptographically secure random alphanumeric string.
 *
 * @param length - Desired string length
 * @param alphabet - Character set to use (defaults to uppercase alphanumeric)
 * @returns Random string of specified length
 *
 * @example
 * const suffix = generateSecureAlphanumeric(8); // "A3K9Q7F2"
 * const token = generateSecureAlphanumeric(32, '0123456789abcdef'); // hex
 */
export function generateSecureAlphanumeric(
  length: number,
  alphabet: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
): string {
  const chars: string[] = [];

  for (let i = 0; i < length; i++) {
    const index = getSecureRandomInt(alphabet.length);
    chars.push(alphabet[index]);
  }

  return chars.join('');
}

/**
 * Generate a cryptographically secure random base64url string.
 * Useful for PKCE code verifiers, state tokens, etc.
 *
 * @param byteLength - Number of random bytes to generate
 * @returns Base64url-encoded string (no padding)
 *
 * @example
 * const codeVerifier = generateSecureBase64Url(32); // 43 chars
 * const state = generateSecureBase64Url(16); // 22 chars
 */
export function generateSecureBase64Url(byteLength: number): string {
  if (byteLength <= 0) {
    throw new Error('byteLength must be positive');
  }

  // Browser environment
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(byteLength);
    window.crypto.getRandomValues(bytes);

    return base64UrlEncode(bytes);
  }

  // Node.js environment
  if (typeof require !== 'undefined') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const nodeCrypto = require('crypto');
      const bytes = nodeCrypto.randomBytes(byteLength);

      return base64UrlEncode(bytes);
    } catch (err) {
      throw new Error('Failed to generate random bytes in Node.js');
    }
  }

  throw new Error('No secure RNG available');
}

/**
 * Convert bytes to base64url encoding (RFC 4648 §5).
 *
 * @param bytes - Byte array to encode
 * @returns Base64url-encoded string (no padding)
 */
function base64UrlEncode(bytes: Uint8Array | Buffer): string {
  // Convert to regular base64
  let base64: string;

  if (typeof Buffer !== 'undefined' && bytes instanceof Buffer) {
    base64 = bytes.toString('base64');
  } else if (bytes instanceof Uint8Array) {
    // Convert Uint8Array to base64 in browser
    const binaryString = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    base64 = btoa(binaryString);
  } else {
    throw new Error('Invalid bytes type');
  }

  // Convert to base64url: replace +/= with -_
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}
